import { APP_CONSTANTS } from "@core/constants/app-constants";
import { useI18n } from "@core/i18n/i18n";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog, DIALOG_SIZE } from "@ui/components/dialog/base-dialog";
import { TextAreaWithCharLimit } from "@ui/components/common/text-area-with-char-limit";
import { row, stack } from "@ui/style";
import { motion } from "framer-motion";
import { DialogProps } from "primereact/dialog";
import React, { useMemo } from "react";
import { Controller, useForm } from "react-hook-form";

interface FormData {
	programName: string;
}

export interface RenameProgramDialogProps extends DialogProps {
	programName: string;
	renameProgram?: (newProgramName: string) => void;
	isTemplate?: boolean;
}

export const RenameProgramDialog = ({
	programName,
	renameProgram,
	isTemplate,
	...otherProps
}: RenameProgramDialogProps): JSX.Element => {
	const { format } = useI18n();

	const { control, handleSubmit } = useForm<FormData>({
		defaultValues: { programName: programName },
	});

	const onSubmit = (data: FormData) => {
		if (renameProgram) {
			renameProgram(data.programName);
			otherProps.onHide();
		}
	};

	const translationKeys = useMemo(
		() =>
			isTemplate
				? {
						title: format("templateBuilder.renameProgramDialog.title"),
						label: format("templateBuilder.renameProgramDialog.newName.label"),
						placeholder: format("templateBuilder.renameProgramDialog.newName.placeholder"),
						saveButton: format("templateBuilder.renameProgramDialog.saveButton"),
					}
				: {
						title: format("programBuilder.renameProgramDialog.title"),
						label: format("programBuilder.renameProgramDialog.newName.label"),
						placeholder: format("programBuilder.renameProgramDialog.newName.placeholder"),
						saveButton: format("programBuilder.renameProgramDialog.saveButton"),
					},
		[format, isTemplate],
	);

	return (
		<BaseDialog
			header={translationKeys.title}
			visible={true}
			footer={
				<ButtonContainer>
					<Button label={translationKeys.saveButton} type="submit" onClick={handleSubmit(onSubmit)} />
				</ButtonContainer>
			}
			width={DIALOG_SIZE.S}
			{...otherProps}
		>
			<DialogContainer>
				<Form onSubmit={handleSubmit(onSubmit)}>
					<FormField>
						<label className="p-size-m">{translationKeys.label}</label>
						<Controller
							name="programName"
							control={control}
							defaultValue={programName}
							rules={{
								required: format("global.form.field.requiredError"),
								maxLength: {
									value: APP_CONSTANTS.PROGRAM_OR_TEMPLATE_MAX_TITLE_LENGTH,
									message: format("global.programName.maxLength"),
								},
							}}
							render={({ field: { onChange, value, ...otherFields }, fieldState }) => (
								<TextAreaWithCharLimit
									{...otherFields}
									autoComplete="off"
									autoFocus={!value}
									text={value}
									onTextChange={onChange}
									errorMessage={fieldState.error?.message}
									charLimit={APP_CONSTANTS.PROGRAM_OR_TEMPLATE_MAX_TITLE_LENGTH}
									placeholder={translationKeys.placeholder}
								/>
							)}
						/>
					</FormField>
				</Form>
			</DialogContainer>
		</BaseDialog>
	);
};

const DialogContainer = styled.div`
	${stack("M", "flexstart", "stretch")};
	height: 100%;
`;

const ButtonContainer = styled.div`
	padding-top: 1.25rem;
	${row("S", "flexend")}
`;

const Form = styled(motion.form)`
	${stack("M", "spacebetween", "stretch")}
	height: 100%;
`;

const FormField = styled.div`
	${stack("S", "flexstart", "stretch")};
`;
